// src/pages/admin/GlobalRewards.jsx
// One common reward-tier set applied to ALL dealers. Admin edits here once;
// every dealer sees the same rewards (in SOTA Vyapar Sales).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/axios";
import toast from "react-hot-toast";

export default function GlobalRewards() {
  const navigate = useNavigate();
  const adminEmail = localStorage.getItem("adminEmail") || "";

  const [rows, setRows] = useState([]); // { fromKg, toKg, rewards }
  const [fromKg, setFromKg] = useState("");
  const [toKg, setToKg] = useState("");
  const [rewardsInput, setRewardsInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/global-rewards");
      if (res.data?.success) {
        setRows(
          (res.data.tiers || []).map((t) => ({
            fromKg: t.fromKg,
            toKg: t.toKg ?? "",
            rewards: Array.isArray(t.rewards) ? t.rewards.join(" / ") : "",
          }))
        );
      }
    } catch (e) {
      toast.error("Failed to load rewards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRow = () => {
    if (!fromKg || !rewardsInput.trim()) {
      toast.error("From KG and reward(s) required");
      return;
    }
    if (toKg && Number(toKg) <= Number(fromKg)) {
      toast.error("To KG must be greater than From KG");
      return;
    }
    setRows((prev) => [
      ...prev,
      { fromKg: Number(fromKg), toKg: toKg === "" ? "" : Number(toKg), rewards: rewardsInput.trim() },
    ]);
    setFromKg("");
    setToKg("");
    setRewardsInput("");
  };

  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const tiers = rows.map((r) => ({
        fromKg: Number(r.fromKg),
        toKg: r.toKg === "" || r.toKg == null ? null : Number(r.toKg),
        rewards: String(r.rewards || "")
          .split("/")
          .map((s) => s.trim())
          .filter(Boolean),
      }));
      const res = await api.put("/admin/global-rewards", { tiers, updatedBy: adminEmail });
      if (res.data?.success) {
        toast.success("Rewards saved for all dealers ✓");
        load();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate("/admin-dashboard")}
            className="h-9 w-9 rounded-full border bg-white font-bold hover:bg-gray-50"
          >
            ←
          </button>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">🎁 Rewards (All Dealers)</h1>
            <p className="text-xs text-gray-500">Ye reward tiers sabhi dealers pe lagti hain.</p>
          </div>
        </div>

        {/* Add tier */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-5">
          <p className="text-sm font-bold text-gray-800 mb-2">Add a tier</p>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input
              type="number"
              value={fromKg}
              onChange={(e) => setFromKg(e.target.value)}
              placeholder="From KG *"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <input
              type="number"
              value={toKg}
              onChange={(e) => setToKg(e.target.value)}
              placeholder="To KG (blank = & above)"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
          </div>
          <input
            value={rewardsInput}
            onChange={(e) => setRewardsInput(e.target.value)}
            placeholder="Reward(s) — multiple separated by /  e.g. Cooker / Mixer"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
          />
          <button
            onClick={addRow}
            className="px-4 py-2 rounded-lg bg-gray-800 text-white font-bold text-sm hover:bg-gray-900"
          >
            + Add tier
          </button>
        </div>

        {/* Tiers list */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-4">
          {loading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No tiers yet. Add above.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-amber-50 text-amber-800">
                <tr className="text-left">
                  <th className="px-4 py-2 font-bold">Target (KG)</th>
                  <th className="px-4 py-2 font-bold">Reward</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...rows]
                  .sort((a, b) => (a.fromKg || 0) - (b.fromKg || 0))
                  .map((r, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 font-semibold whitespace-nowrap">
                        {r.toKg
                          ? `${Number(r.fromKg).toLocaleString()} – ${Number(r.toKg).toLocaleString()} KG`
                          : `${Number(r.fromKg).toLocaleString()}+ KG`}
                      </td>
                      <td className="px-4 py-2.5 text-yellow-700 font-semibold">{r.rewards || "—"}</td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => removeRow(rows.indexOf(r))}
                          className="text-red-600 text-xs font-bold hover:underline"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="w-full bg-emerald-600 text-white font-bold py-3 rounded-xl hover:bg-emerald-700 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Rewards (All Dealers)"}
        </button>
      </div>
    </div>
  );
}
